"use client";

import React, { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Sparkles, ArrowRight } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function EmergencyBoilerRepairs() {
  const {
    ref: titleRef,
    isVisible: titleVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: textRef,
    isVisible: textVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: btnRef,
    isVisible: btnVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: imgRef,
    isVisible: imgVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const [offsetY, setOffsetY] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      setOffsetY(window.scrollY);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section data-section-id="240430" id="emergency-boiler-repairs" className="dark relative min-h-[90vh] w-full overflow-hidden bg-gradient-to-br from-destructive/20 via-background to-background flex items-center pt-24 pb-16 lg:pt-32 lg:pb-24">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-5%] w-[40%] h-[40%] rounded-lg bg-destructive/10 blur-3xl" />
        <div className="absolute bottom-[-10%] right-[-5%] w-[50%] h-[50%] rounded-lg bg-primary/10 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="mb-8 p-4 rounded-lg bg-muted/40 border border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground font-default">
            Notice: This is an older version of the page. Our new consolidated page offers full details and updated Dublin boiler repair services.
          </p>
          <Link to="/BoilerRepairDublinPage" className="text-sm font-medium text-primary hover:underline inline-flex items-center shrink-0">
            Visit New Consolidated Page
            <ArrowRight className="w-4 h-4 ml-1.5" />
          </Link>
        </div>

        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-8">
          
          <div className="w-full lg:w-[60%] flex flex-col justify-center space-y-8">
            <div className="space-y-6">
              <div ref={titleRef} className={`transition-all duration-1000 ease-out ${titleVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
                <h1 className="text-5xl md:text-6xl lg:text-7xl xl:text-[80px] font-bold font-default leading-[1.1] text-foreground tracking-tighter">
                  Boiler Broken? <br className="hidden md:block" />
                  <span className="text-destructive relative inline-block">
                    Rapid Repair
                    <svg className="absolute w-full h-3 -bottom-1 left-0 text-destructive/30 -z-10" viewBox="0 0 100 10" preserveAspectRatio="none">
                      <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="8" fill="none" strokeLinecap="round" />
                    </svg>
                  </span>
                </h1>
              </div>

              <p ref={textRef} className={`text-lg md:text-xl text-muted-foreground max-w-2xl font-default leading-relaxed transition-all duration-1000 delay-200 ease-out ${textVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
                D4 Plumbing & Heating provides urgent emergency boiler repairs across Dublin. 
                Our RGI registered engineers are on standby to restore your heating and hot water 
                with professional, fully insured service you can trust.
              </p>
            </div>

            <div ref={btnRef} className={`flex flex-col sm:flex-row items-start sm:items-center gap-4 transition-all duration-1000 delay-400 ease-out ${btnVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <Button size="lg" className="h-14 px-8 text-base rounded-md shadow-lg shadow-destructive/20 bg-destructive hover:bg-destructive/90 text-destructive-foreground" asChild>
                <Link to="tel:+35319081819">
                  Call 01 908 1819
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="h-14 px-8 text-base rounded-md bg-background/50 backdrop-blur-sm border-border hover:bg-accent/20" asChild>
                <Link to="/BoilerRepairPage">
                  View Repair Services
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </div>
            
            <div className={`pt-8 flex items-center gap-6 transition-all duration-1000 delay-500 ease-out ${btnVisible ? "opacity-100" : "opacity-0"}`}>
              <div className="flex -space-x-3">
                {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="w-10 h-10 rounded-md border-2 border-background bg-muted overflow-hidden">
                    <img src={`/img/dublin_client_review_${i}.png`} alt="Satisfied Dublin customer" className="w-full h-full object-cover" />
                  </div>)}
              </div>
              <div className="text-sm font-medium text-muted-foreground font-mono">
                RGI Registered Dublin Engineers
              </div>
            </div>
          </div>

          <div className="w-full lg:w-[40%] relative mt-12 lg:mt-0">
            <div ref={imgRef} className={`relative w-full max-w-md mx-auto lg:max-w-none transition-all duration-1000 delay-300 ease-out ${imgVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`} style={{
            transform: `translateY(${offsetY * 0.05}px)`
          }}>
              <div className="relative animate-[float_6s_ease-in-out_infinite]">
                <div className="relative aspect-[4/5] w-full rounded-lg overflow-hidden shadow-2xl shadow-primary/20 border-8 border-border/50 backdrop-blur-sm">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689081/img/emergency_boiler_repair_dublin.png" alt="Professional D4 Plumbing engineer repairing a gas boiler" className="w-full h-full object-cover object-center" />
                  
                  <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent pointer-events-none" />
                </div>

                <div className="absolute -bottom-6 -left-6 bg-card rounded-md p-4 shadow-xl border border-border/50 flex items-center gap-4 animate-[float_5s_ease-in-out_infinite_reverse]">
                  <div className="w-12 h-12 rounded-md bg-primary/20 flex items-center justify-center text-2xl">
                    <Sparkles className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-foreground font-default">RGI Registered</p>
                    <p className="text-xs text-muted-foreground font-mono">Certified Safety</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <style dangerouslySetInnerHTML={{
      __html: `
        @keyframes float {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
          100% { transform: translateY(0px); }
        }
      `
    }} />
    </section>;
}